import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import { registrarRevision } from '../api/revision'
import ConversationList from '../components/inbox/ConversationList'
import ChatThread from '../components/inbox/ChatThread'
import SelectorAsesor from '../components/inbox/SelectorAsesor'
import AvisoSinConexion from '../components/inbox/AvisoSinConexion'
import type { ContextTab } from '../components/inbox/ContextPanel'
import PanelDelCaso from '../components/inbox/PanelDelCaso'
import { CHAT_MINIMO_EN_VIVO } from '../disposicion'
import EmptyState from '../components/EmptyState'
import { useAuth } from '../auth/useAuth'
import type {
  Advisor, ConversationListResponse, ConversationSummary,
} from '../types/portal'
import { useCopiloto } from '../useCopiloto'
import { useHilo } from '../useHilo'
import { MENSAJE_NUEVO } from '../eventos'
import { delServidor, type VistosDelServidor } from '../noLeidos'
import { enLaCola } from '../cola'
import { QUE_ES_UN_CLIENTE_ESPERANDO, esperandoFueraDePlazo } from '../sla'

/**
 * "En vivo" — monitoreo de las conversaciones del equipo (supervisor y admin).
 *
 * Reutiliza el layout de 3 columnas de la bandeja del asesor en modo SOLO
 * LECTURA: el supervisor lee el hilo completo, el contexto de la IA y los
 * documentos sin escribirle al cliente.
 *
 * Diferencias con Mi bandeja:
 *   - la lista trae TODAS las del equipo, incluidas las sin asignar
 *   - cada ítem muestra qué asesor atiende
 *   - sin compositor, sin plantillas, sin sugerencia de IA, sin resolver
 *   - en su lugar, la barra de monitoreo con "Intervenir" y "Reasignar"
 *   - validar o rechazar documentos SÍ (Ruben, 20-oct-2026): es una decisión
 *     sobre el archivo, no una atención, así que no toma la conversación ni le
 *     escribe al cliente
 *
 * Lo que va a la ruta `/live` es solo Supervisor y Admin (`App.tsx`).
 *
 * La selección es independiente de la de la bandeja: entrar a auditar no debe
 * cambiar la conversación que el asesor tiene abierta en su pantalla.
 */

const POLL_MS = 20_000

export default function LiveMonitor() {
  const { user, canSupervise } = useAuth()
  const navigate = useNavigate()

  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  // Lo que quien mira ya vio, como llegó con la lista: el número verde.
  const [vistosServidor, setVistosServidor] = useState<VistosDelServidor | null>(null)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  // `monitorId`, no `selectedId`: el nombre importa porque es un estado
  // distinto del de la bandeja (ver el modelo de estado del handoff).
  const [monitorId, setMonitorId] = useState<string | null>(null)
  // El hilo que el supervisor está mirando se refresca solo —el punto de «En
  // vivo» es ver la conversación mientras pasa—, con el cableado de la bandeja.
  const hilo = useHilo(monitorId)
  const { detail, refrescar: refrescarHilo, reintentar: reintentarHilo } = hilo

  const [tab, setTab] = useState<ContextTab>('copiloto')
  // El mismo cableado que la bandeja (DEF-029): al día con el hilo, no solo
  // al abrirlo.
  const copiloto = useCopiloto(monitorId, detail, tab === 'copiloto')

  const [accionBusy, setAccionBusy] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [reasignando, setReasignando] = useState(false)
  const [advisors, setAdvisors] = useState<Advisor[]>([])
  const [reviewingDoc, setReviewingDoc] = useState<string | null>(null)

  // ── Listado: todas las del equipo ─────────────────────────────────────────

  const fetchList = useCallback(async (silencioso = false) => {
    if (!silencioso) setListLoading(true)
    try {
      const res = await apiClient.get<ConversationListResponse>('/conversations?limit=100')
      setConversations(res.data.conversations ?? [])
      setVistosServidor(delServidor(res.data))
      setListError(null)
    } catch (err) {
      setListError(errorMessage(err, 'No se pudo cargar el monitoreo.'))
    } finally {
      if (!silencioso) setListLoading(false)
    }
  }, [])

  useEffect(() => { void fetchList() }, [fetchList])
  useEffect(() => {
    const id = setInterval(() => { void fetchList(true) }, POLL_MS)
    // Un cliente escribió: la fila sube con su número verde junto con el
    // sonido del Layout, no hasta 20 s después.
    const refrescar = () => { void fetchList(true) }
    window.addEventListener(MENSAJE_NUEVO, refrescar)
    return () => {
      clearInterval(id)
      window.removeEventListener(MENSAJE_NUEVO, refrescar)
    }
  }, [fetchList])

  // ── Detalle y copiloto ────────────────────────────────────────────────────

  const seleccionar = useCallback((id: string) => {
    setMonitorId(id)
    setTab('copiloto')
    setAviso(null)
    setReasignando(false)
  }, [])

  // `?c=<id>` lo trae el pop-up de «un cliente escribió» (`avisos.ts`,
  // `destinoDelAviso`): la conversación de otro asesor, o sin asignar, se mira
  // acá. Se consume al abrirla, para que no mande sobre la selección siguiente.
  const [params, setParams] = useSearchParams()
  const pedida = params.get('c')
  useEffect(() => {
    if (!pedida || listLoading) return
    if (conversations.some((c) => c.conversation_id === pedida)) seleccionar(pedida)
    setParams({}, { replace: true })
  }, [pedida, listLoading, conversations, seleccionar, setParams])

  // ── Acciones ──────────────────────────────────────────────────────────────

  const miEmail = (user?.email ?? '').toLowerCase()
  const esMia = Boolean(detail?.assigned_to && detail.assigned_to.toLowerCase() === miEmail)

  // INTERVENIR TE LLEVA A DONDE SE ESCRIBE
  //
  // «En vivo» es de solo lectura a propósito, y hasta el 28-set-2026
  // intervenir terminaba ahí: la conversación pasaba a ser tuya y seguías
  // mirando la barra de «Solo lectura», sin compositor. A un admin que no está
  // en el grupo Asesor ni siquiera le aparecía «Mi bandeja» en el menú, así que
  // no tenía dónde contestarle al cliente que acababa de tomar.
  const abrirEnMiBandeja = useCallback((id: string) => {
    navigate(`/inbox?c=${encodeURIComponent(id)}`)
  }, [navigate])

  const intervenir = useCallback(async () => {
    if (!detail) return
    if (esMia) {
      abrirEnMiBandeja(detail.conversation_id)
      return
    }
    setAccionBusy(true)
    setAviso(null)
    try {
      await apiClient.post<{ previous_assignee: string | null }>(
        `/conversations/${detail.conversation_id}/intervene`, {},
      )
      // El aviso de a quién se le sacó lo da la auditoría; acá lo que sigue
      // es contestar.
      abrirEnMiBandeja(detail.conversation_id)
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo intervenir.'))
      setAccionBusy(false)
    }
  }, [detail, esMia, abrirEnMiBandeja])

  const abrirReasignar = useCallback(async () => {
    setReasignando(true)
    setAviso(null)
    try {
      const res = await apiClient.get<{ advisors: Advisor[] }>('/advisors')
      setAdvisors(res.data.advisors ?? [])
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo cargar la lista de asesores.'))
      setReasignando(false)
    }
  }, [])

  const reasignarA = useCallback(async (email: string) => {
    if (!detail) return
    setAccionBusy(true)
    try {
      await apiClient.post(`/conversations/${detail.conversation_id}/assign`,
                           { target_email: email })
      setAviso(`Reasignada a ${email.split('@')[0]}.`)
      setReasignando(false)
      refrescarHilo()
      void fetchList(true)
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo reasignar.'))
    } finally {
      setAccionBusy(false)
    }
  }, [detail, refrescarHilo, fetchList])

  // VALIDAR O RECHAZAR SIN TOMAR LA CONVERSACIÓN (Ruben, 20-oct-2026)
  //
  // Supervisión decide sobre un archivo desde acá, como el ejecutivo en su
  // bandeja y con la misma llamada (`api/revision.ts`): definitiva, a su
  // nombre. No interviene: la conversación sigue con su asesor y al cliente no
  // se le escribe. «Pedir de nuevo» SÍ es un mensaje al cliente, así que acá
  // no se ofrece; quien quiera pedirlo interviene.
  const revisarDoc = useCallback(async (
    docId: string, accion: 'approve' | 'reject' | 'request', motivo = '',
  ) => {
    if (accion === 'request') return
    setReviewingDoc(docId)
    setAviso(null)
    try {
      await registrarRevision(docId, accion, motivo)
      refrescarHilo()
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo registrar la revisión.'))
    } finally {
      setReviewingDoc(null)
    }
  }, [refrescarHilo])

  // Lo que espera que alguien le asigne un asesor: la MISMA regla que la Cola
  // global (`cola.ts`). Contaba toda conversación sin dueño, y el 13-oct-2026
  // anunció «2 sin asesor… para repartirlas» con la cola vacía: eran clientes
  // contestando las preguntas del bot, que todavía no tienen por qué tener
  // asesor (DEF-007 lo asigna al nacer el expediente). Antes de eso contaba las
  // históricas: una alerta que siempre está encendida deja de ser una alerta.
  const sinAsignar = useMemo(() => conversations.filter(enLaCola).length, [conversations])
  // Clientes esperando fuera de plazo, con la regla de todas las pantallas
  // (`sla.esperandoFueraDePlazo`, DEF-112). Contaba `sla.level`, que solo ve
  // el primer mensaje: el cliente que volvió a escribir después de que el
  // asesor le contestó no aparecía acá y sí en Asesores.
  const vencidas = useMemo(
    () => conversations.filter(esperandoFueraDePlazo),
    [conversations],
  )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* El SLA vencido gana sobre "sin asignar": alguien está esperando fuera
          de plazo y eso es más urgente que repartir la cola. */}
      {!aviso && vencidas.length > 0 && (
        <div className="flex shrink-0 items-center gap-3 border-b border-danger-border bg-danger-light px-[18px] py-2.5">
          <p className="flex-1 text-body text-danger" title={QUE_ES_UN_CLIENTE_ESPERANDO}>
            <b>
              {vencidas.length} cliente{vencidas.length === 1 ? '' : 's'} sin respuesta
              fuera de plazo.
            </b>{' '}
            Pasan solos a otro asesor que no haya dejado vencer el plazo hoy; si no
            queda ninguno, quedan «para revisar» con su asesor y en Incidencias.
            <span className="mt-0.5 block text-meta">{QUE_ES_UN_CLIENTE_ESPERANDO}</span>
          </p>
        </div>
      )}

      {(aviso || sinAsignar > 0) && (
        <div className="flex shrink-0 items-center gap-3 border-b border-info-border bg-info-light px-[18px] py-2.5">
          <p className="flex-1 text-body text-info">
            {aviso ?? (
              <>
                <b>
                  {sinAsignar} conversación{sinAsignar === 1 ? '' : 'es'} esperando que les
                  asignen un asesor.
                </b>{' '}
                Repártelas desde Operación › Cola global.
              </>
            )}
          </p>
          {aviso && (
            <button onClick={() => setAviso(null)} className="text-body font-bold text-info">
              Cerrar
            </button>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <ConversationList
          conversations={conversations}
          selectedId={monitorId}
          onSelect={seleccionar}
          loading={listLoading}
          error={listError}
          variant="monitor"
          myEmail={user?.email ?? ''}
          vistosDelServidor={vistosServidor}
        />

        {/* `relative`: el cajón del panel se monta encima del chat, no de la
            lista (DEF-031, `PanelDelCaso`). */}
        <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {hilo.cargando ? (
            <Mensaje titulo="Cargando conversación…" />
          ) : hilo.error ? (
            <Mensaje
              titulo="No se pudo abrir la conversación"
              detalle={hilo.error}
              onRetry={reintentarHilo}
            />
          ) : !detail ? (
            <div className="flex flex-1 items-center justify-center p-10">
              <EmptyState
                icon="👁️"
                title="Elige una conversación para monitorear"
                description={
                  'Verás el hilo completo, el contexto de la IA y los documentos, '
                  + 'en solo lectura. Puedes validar o rechazar documentos sin tomar '
                  + 'la conversación, e intervenir o reasignar si hace falta.'
                }
              />
            </div>
          ) : (
            <>
              {/* 412 y no 444 (DEF-031, 07-oct-2026): es lo que el chat
                  puede pedir con el panel en cajón (`CHAT_MINIMO_EN_CAJON`).
                  En columna igual tiene sus 444: la decisión los exige. */}
              <div className="flex min-h-0 min-w-[412px] flex-1 flex-col">
                {hilo.sinConexion && <AvisoSinConexion />}
                <ChatThread
                  detail={detail}
                  readOnly
                  monitor={{
                    onIntervene: () => void intervenir(),
                    onReassign: () => void abrirReasignar(),
                    busy: accionBusy,
                    esMia,
                  }}
                  // En solo lectura no hay compositor: estos props existen por
                  // la firma compartida con la bandeja y no se usan.
                  draft=""
                  onDraftChange={() => {}}
                  onSend={() => {}}
                  sending={false}
                  sendError={null}
                  suggestion={null}
                  onUseSuggestion={() => {}}
                  onDismissSuggestion={() => {}}
                  templates={[]}
                  onResolve={() => {}}
                  resolving={false}
                  onReactivate={() => {}}
                  reactivating={false}
                />
                {reasignando && (
                  <SelectorAsesor
                    advisors={advisors}
                    actual={detail.assigned_to}
                    busy={accionBusy}
                    onElegir={(email) => void reasignarA(email)}
                    onCancelar={() => setReasignando(false)}
                  />
                )}
              </div>
              {/* El chat de En vivo pide más ancho —su cabecera lleva el chip
                  largo de solo lectura—, así que el panel pasa a cajón más
                  tarde que en la bandeja. */}
              <PanelDelCaso
                chatMinimo={CHAT_MINIMO_EN_VIVO}
                detail={detail}
                copilot={copiloto.copilot}
                copilotLoading={copiloto.cargando}
                copilotUpdating={copiloto.actualizando}
                onRetryCopilot={copiloto.reintentar}
                onUseAction={() => {}}
                onReviewDoc={(id, accion, motivo) => void revisarDoc(id, accion, motivo)}
                reviewingDoc={reviewingDoc}
                onDocumentoSubido={() => {}}
                tab={tab}
                onTabChange={setTab}
                readOnly
                puedeDecidir={canSupervise}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Mensaje({ titulo, detalle, onRetry }: {
  titulo: string; detalle?: string; onRetry?: () => void
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
      <p className="text-page font-extrabold text-ink">{titulo}</p>
      {detalle && <p className="max-w-md text-body text-ink-muted">{detalle}</p>}
      {onRetry && <button onClick={onRetry} className="btn-ghost">Reintentar</button>}
    </div>
  )
}
